import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/cn";

export type MorphPanelAnchor =
  | "top-left"
  | "top-right"
  | "top-center"
  | "bottom-left"
  | "bottom-right"
  | "bottom-center";

const panePinnedTo: Record<MorphPanelAnchor, string> = {
  "top-left": "left-0 top-0",
  "top-right": "right-0 top-0",
  "top-center": "left-1/2 top-0 -translate-x-1/2",
  "bottom-left": "bottom-0 left-0",
  "bottom-right": "bottom-0 right-0",
  "bottom-center": "bottom-0 left-1/2 -translate-x-1/2",
};

const isSize = (property: string) => property === "width" || property === "height";

/**
 * A panel exactly the size of the pane it is showing.
 *
 * Changing `view` swaps the pane and takes the panel to the new size, so a
 * title that opens into settings, or a chip that opens into a card and then a
 * page, reads as one object growing rather than a stack of panels replacing
 * each other. Only the size moves.
 *
 * Something inside the pane that animates its own width or height is followed
 * frame by frame rather than chased with a second transition, so the panel's
 * edge stays on it instead of trailing behind.
 */
export function MorphPanel({
  view,
  anchor,
  growFrom = "center",
  paneRef,
  ref,
  className,
  children,
}: {
  /** Changing this swaps the pane and resizes the panel to match. */
  view: string;
  /** The edge the pane is pinned to, which is the edge that stays put. */
  anchor: MorphPanelAnchor;
  /** Transform origin for the incoming pane. See `growOrigin`. */
  growFrom?: string;
  /** The live pane, for measuring where a tile inside it sits. */
  paneRef?: React.RefObject<HTMLDivElement | null>;
  /** The panel itself, for a caller that needs to know what is inside it. */
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  children: React.ReactNode;
}) {
  /** The size to hold, and whether reaching it means getting smaller. */
  const [size, setSize] = useState<{ width: number; height: number; shrinking: boolean }>();
  /** False for the first frame: arriving at a size is not a change to animate. */
  const [morphing, setMorphing] = useState(false);
  const panel = useRef<HTMLDivElement | null>(null);
  /** Elements inside the pane whose width or height is mid-transition. */
  const movingInside = useRef(new Set<Element>());
  const callerRef = useRef(ref);
  callerRef.current = ref;

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMorphing(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const holdPanel = useCallback((element: HTMLDivElement | null) => {
    panel.current = element;
    const forward = callerRef.current;
    if (typeof forward === "function") forward(element);
    else if (forward) forward.current = element;
    if (!element) return;
    const moving = movingInside.current;
    const start = (event: TransitionEvent) => {
      if (event.target !== element && isSize(event.propertyName))
        moving.add(event.target as Element);
    };
    const stop = (event: TransitionEvent) => {
      if (isSize(event.propertyName)) moving.delete(event.target as Element);
    };
    element.addEventListener("transitionrun", start);
    element.addEventListener("transitionend", stop);
    element.addEventListener("transitioncancel", stop);
    return () => {
      element.removeEventListener("transitionrun", start);
      element.removeEventListener("transitionend", stop);
      element.removeEventListener("transitioncancel", stop);
      moving.clear();
      panel.current = null;
      if (typeof forward === "function") forward(null);
      else if (forward) forward.current = null;
    };
  }, []);

  /** Re-measures on every swap, and while a pane grows as it is typed into. */
  const measurePane = useCallback(
    (pane: HTMLDivElement) => {
      if (paneRef) paneRef.current = pane;
      const measure = (swapped: boolean) => {
        const width = pane.offsetWidth;
        const height = pane.offsetHeight;
        const element = panel.current;
        const moving = movingInside.current;
        for (const inside of moving) if (!inside.isConnected) moving.delete(inside);
        // Written straight to the panel, in the same frame the pane changed, with no transition
        // of its own: a render later, or eased, it would lag what it follows.
        const following = !swapped && moving.size > 0;
        if (element) {
          element.style.transition = following ? "none" : "";
          if (following) {
            element.style.width = `${width}px`;
            element.style.height = `${height}px`;
          }
        }
        setSize(current => {
          if (current?.width === width && current.height === height) return current;
          const shrinking =
            current !== undefined && width * height < current.width * current.height;
          return { width, height, shrinking };
        });
      };
      measure(true);
      const observer = new ResizeObserver(() => measure(false));
      observer.observe(pane);
      return () => observer.disconnect();
    },
    [paneRef],
  );

  return (
    <div
      ref={holdPanel}
      style={{ width: size?.width, height: size?.height }}
      className={cn(
        "relative overflow-hidden",
        morphing &&
          cn(
            "transition-[width,height]",
            // Closing is quicker, and lands rather than coasting in.
            size?.shrinking
              ? "duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
              : "duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          ),
        className,
      )}
    >
      <div className={cn("absolute w-max", panePinnedTo[anchor])}>
        <div
          key={view}
          ref={measurePane}
          style={{ transformOrigin: growFrom }}
          className="animate-in fade-in zoom-in-95 duration-200 ease-out"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
